import { Component, Input, SimpleChanges, TrackByFunction } from '@angular/core';
import { provideSortComponent } from '@dice/app/directives/table-sort.directive';
import { CoreTableFilter } from '@dice/app/libraries/core-table-virtual-scrolling/filter/filter';
import { VirtualTable } from '@dice/app/libraries/core-table-virtual-scrolling/virtual-table';
import { UnaryFunction } from '@dice/app/types/dice.types';
import { HashMap } from '@dice/app/types/hash-map';
import {
  Column,
  HeaderContent,
  RowContent,
  TableConfiguration,
  TableSortDefinition,
  TableSortInterface,
} from '@dice/app/types/table.types';
import { has as _has } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'virtual-scroll-table',
  templateUrl: './virtual-scroll-table.component.html',
  providers: [provideSortComponent(VirtualScrollTableComponent)],
  standalone: false,
})
export class VirtualScrollTableComponent<T> extends VirtualTable<T> implements TableSortInterface<T> {
  @Input() set data(data: T[]) {
    this.set(data);
  }

  /**
   * Important to set renderTable to true, only when the table is actually being shown on the screen.
   * The Virtual Scroll logic tries to read the visible viewport on initialization. When no viewport is available
   * the result is a faulty view.
   */
  @Input() columns: Column[];

  @Input() trackBy: TrackByFunction<T>;

  @Input() tableDefinition: TableConfiguration;

  @Input() searchTerm: string;

  @Input() rowHeight: number;

  @Input() offset: number;

  @Input() defaultSorting: TableSortDefinition;

  @Input() disableSort: boolean = false;

  /**
   * if you hide the header, then set disableSort = true. Otherwise error appears in the console
   */
  @Input() hideHeader: boolean = false;

  @Input() itemFilters: HashMap<CoreTableFilter<T>>;

  header: string[] = [];

  headerOffset: Observable<number>;

  private filterParameters: CoreTableFilter<T> = null;

  constructor() {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { columns, searchTerm } = changes;
    if (!!columns && columns.firstChange) {
      this.columns.forEach((column: Column) => {
        this.header.push(column.name);
      });
    }
    if (searchTerm) {
      this.clearFilters();
      if (this.searchTerm && this.searchTerm.length > 0) {
        this.setQueryFilter();
      }
    }
    if (this.itemFilters) {
      this.dataSource.setFilterOnMultipleKeys(this.itemFilters, false);
    }
  }

  ngAfterViewInit(): void {
    this.headerOffset = this.viewport.renderedRangeStream.pipe(
      map(() => -this.viewport.getOffsetToRenderedContentStart()),
    );
  }

  get rowHasClickHandler(): boolean {
    return _has(this, 'tableDefinition.tableRow.click');
  }

  updateClasses(rowData: T): string {
    if (!_has(this, 'tableDefinition.tableRow.cssClass')) {
      return '';
    }
    const {
      tableRow: { cssClass },
    } = this.tableDefinition;
    return this.getUpdatedCssClasses(cssClass, rowData);
  }

  getColumnCssClass(table: HeaderContent | RowContent, rowData: T): string | string[] {
    if (!_has(table, 'columnCssClass')) {
      return '';
    }
    const { columnCssClass } = table;
    return this.getUpdatedCssClasses(columnCssClass, rowData);
  }

  private getUpdatedCssClasses(cssClassDefinition: string | string[] | UnaryFunction<T, string>, rowData: T): string {
    if (cssClassDefinition) {
      if (typeof cssClassDefinition === 'string') {
        return cssClassDefinition;
      }
      if (typeof cssClassDefinition === 'function') {
        return cssClassDefinition(rowData);
      }
      if (Array.isArray(cssClassDefinition)) {
        return cssClassDefinition.join(' ');
      }
    }
    return '';
  }

  noop(): void {}

  private setQueryFilter(): void {
    const query = this.searchTerm ? this.searchTerm.toLowerCase() : null;
    const valueFn = (item: T): string => {
      let itemValues = '';
      Object.values(item).forEach((value: any) => {
        if (typeof value === 'string') {
          itemValues += `${value} `;
        }
        if (Array.isArray(value)) {
          itemValues += value.join(' ');
        }
      });
      return itemValues;
    };
    this.filterParameters = this.getFilter('text', query, valueFn);
    const filter = {};
    filter[this.filterParameters.key] = this.filterParameters;
    this.setFilterOnMultipleKeys(filter, false);
  }

  private getFilter(key: string, query: string, valueFn: (item: T) => string): CoreTableFilter<T> {
    const predicate = (value: string): boolean =>
      value && typeof value === 'string' && value.toLowerCase().indexOf(query.toLowerCase()) > -1;
    return { key, predicate, valueFn };
  }
}
